import { useEffect, useState } from "react";
import { NavStep } from "../../../shared-logic/utils/Navigation";
import useStepUrl from "./actions/useStepUrl";
import useSiblingStepLink from "./actions/useSiblingStepLink";
import { getGradeOverviewUrl } from "../../utils/typedUtils";
import { useBnbSelector } from "../../redux/store";
import { isBuildUrl } from "../../utils/navUtils";
import type { CommonSettingsType } from "../../../../../common-deprecated/settings/fetchCommonSettings";

type BackButtonType = { url?: string; step?: NavStep };

const handlePreviousUrl = (
    modelId: string,
    commonSettings: CommonSettingsType,
    previousUrl: string,
    fallbackUrl?: string,
): BackButtonType => {
    const isPreviousUrlBuild = isBuildUrl(previousUrl, commonSettings, modelId);
    // url navigation is checked in this order, because otherwise redirect loops can occur if starting url/point is part of the build app
    if (isPreviousUrlBuild) {
        return { url: fallbackUrl };
    } else {
        return { url: previousUrl || fallbackUrl };
    }
};

// Returns the back button url and step
const useBackButton = (): BackButtonType | null => {
    // We don't fallback to null to avoid not rendering the back button which would create a little jump after first render
    const [backButton, setBackButton] = useState<BackButtonType | null>({ url: "" });
    const step = useBnbSelector((state) => state.navigation.step);
    // The car reducer is not set on the engine step so this grade fallback is needed.
    const modelId = useBnbSelector((state) => state.car?.model?.id || state.grade?.model?.id || state.model.id);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const summaryUrl = useStepUrl(NavStep.Summary).href;
    const prevStep = useSiblingStepLink("prev", true);
    const gradeOverviewUrl = getGradeOverviewUrl(commonSettings, modelId);

    const getBackButtonUrl = (): BackButtonType | null => {
        switch (step) {
            case NavStep.Buy:
                return { url: summaryUrl };
            case NavStep.Submodels:
            case NavStep.Model:
                if (commonSettings.query.codcustomer) {
                    return null;
                } else {
                    // Use the url where the user navigated from, when available
                    const previousUrl = typeof window !== "undefined" ? document.referrer : "";
                    return handlePreviousUrl(modelId, commonSettings, previousUrl, gradeOverviewUrl);
                }
            default:
                return prevStep;
        }
    };

    // We execute this after the first render to make use of the document.referrer
    useEffect(() => setBackButton(getBackButtonUrl()), [step]);

    return backButton;
};

export default useBackButton;
